import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Palette, Check, AlertTriangle, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRESETS = ["#C0392B", "#1F6FEB", "#2E7D6B", "#8E44AD", "#D68910", "#111827", "#BE185D", "#0F766E"];

export default function Settings() {
  const { user, company, setCompany, logout } = useAuth();
  const navigate = useNavigate();
  const [color, setColor] = useState(company.brand_color || "#C0392B");
  const [name, setName] = useState(company.name);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isOwner = user.role === "owner";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/company/settings", { brand_color: color, name });
      setCompany(data.company);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    const msg = isOwner
      ? `Delete your account AND the entire "${company.name}" workspace? This permanently removes your library, renders, team and billing. This cannot be undone.`
      : "Delete your account? You'll lose access and your seat will be freed. This cannot be undone.";
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Your account has been deleted.");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete your account.");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        <p className="overline">Manage</p>
        <h1 className="font-display text-3xl tracking-tight md:text-4xl">Settings</h1>

        <div className="mt-6 space-y-6 rounded-xl border border-[var(--line)] bg-white p-6">
          {isOwner ? (
          <>
          <div>
            <label className="overline">Company name</label>
            <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
          </div>

          <div>
            <label className="overline flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> App accent color</label>
            <p className="mb-3 mt-1 text-xs text-[var(--ink-soft)]">Personalize buttons, highlights and accents across the app.</p>
            <div className="flex flex-wrap items-center gap-3">
              {PRESETS.map((c) => (
                <button key={c} data-testid={`preset-${c}`} onClick={() => setColor(c)}
                  className={`relative h-10 w-10 rounded-full ring-offset-2 transition-transform hover:-translate-y-px ${color.toLowerCase() === c.toLowerCase() ? "ring-2 ring-[var(--ink)]" : "ring-1 ring-[var(--line)]"}`}
                  style={{ backgroundColor: c }}>
                  {color.toLowerCase() === c.toLowerCase() && <Check className="absolute inset-0 m-auto h-4 w-4 text-white" strokeWidth={3} />}
                </button>
              ))}
              <span className="mx-1 h-8 w-px bg-[var(--line)]" />
              <input type="color" data-testid="settings-color" value={color} onChange={(e) => setColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded border border-[var(--line)] bg-white" />
              <input value={color} onChange={(e) => setColor(e.target.value)}
                className="w-28 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
            </div>
          </div>

          <div className="rounded-lg border border-[var(--line)] p-4">
            <p className="overline mb-2">Preview</p>
            <div className="flex items-center gap-3">
              <button style={{ backgroundColor: color }} className="rounded-full px-5 py-2.5 text-sm font-semibold text-white">Generate Render</button>
              <span style={{ color }} className="text-sm font-semibold">Accent text</span>
            </div>
          </div>

          <button data-testid="save-settings-btn" onClick={save} disabled={saving}
            className="rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60">
            {saving ? "Saving…" : "Save settings"}
          </button>
          </>
          ) : (
            <p className="text-sm text-[var(--ink-soft)]">Company branding is managed by your owner. You can manage your own account below.</p>
          )}
        </div>

        <div data-testid="danger-zone" className="mt-6 rounded-xl border border-red-200 bg-red-50/50 p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-red-700">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.75} /> Danger zone
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-[var(--ink-soft)]">
            {isOwner
              ? "Deleting your account permanently removes your entire company workspace — library, renders, team members and billing. This can't be undone."
              : "Deleting your account removes your access and frees your seat. This can't be undone."}
          </p>
          <button data-testid="delete-account-btn" onClick={deleteAccount} disabled={deleting}
            className="mt-4 flex items-center gap-2 rounded-full border border-red-300 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-600 hover:text-white disabled:opacity-60">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />}
            Delete my account
          </button>
        </div>
      </div>
    </div>
  );
}
